/**
 * Hook to detect online/offline status and retry pending sync operations
 */

import { useState, useEffect } from 'react';
import { syncStatus } from '../utils/syncStatus';

/**
 * Monitor online/offline status and retry pending operations when online
 * @param {Object} services - Service layer objects for retry
 * @param {Function} onStatusChange - Callback when online status changes
 */
export function useOnlineStatus(services, onStatusChange) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasPending, setHasPending] = useState(false);

  useEffect(() => {
    // Check for pending operations on mount
    setHasPending(syncStatus.hasPending());

    const handleOnline = async () => {
      console.log('[useOnlineStatus] Back online');
      setIsOnline(true);

      // Notify callback
      if (onStatusChange) {
        onStatusChange({
          isOnline: true,
          hasPending: syncStatus.hasPending(),
          pendingCount: syncStatus.getPendingCount()
        });
      }

      // Retry pending operations if any
      if (syncStatus.hasPending() && services) {
        console.log('[useOnlineStatus] Retrying pending operations...');

        try {
          const results = await syncStatus.retryPending(services);

          console.log('[useOnlineStatus] Retry results:', {
            success: results.success.length,
            failed: results.failed.length
          });

          // Update pending status
          const pendingAfterRetry = syncStatus.hasPending();
          setHasPending(pendingAfterRetry);

          if (onStatusChange) {
            onStatusChange({
              isOnline: true,
              hasPending: pendingAfterRetry,
              pendingCount: syncStatus.getPendingCount()
            });
          }

          // Notify user if there were failures
          if (results.failed.length > 0) {
            console.warn('[useOnlineStatus] Some operations failed to sync:', results.failed);
          }
        } catch (error) {
          console.error('[useOnlineStatus] Error retrying pending operations:', error);
        }
      }
    };

    const handleOffline = () => {
      console.log('[useOnlineStatus] Gone offline');
      setIsOnline(false);

      // Notify callback
      if (onStatusChange) {
        onStatusChange({
          isOnline: false,
          hasPending: syncStatus.hasPending(),
          pendingCount: syncStatus.getPendingCount()
        });
      }
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for custom pending sync events
    const handlePendingChange = () => {
      setHasPending(syncStatus.hasPending());
    };

    window.addEventListener('pendingSyncChanged', handlePendingChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('pendingSyncChanged', handlePendingChange);
    };
  }, [services, onStatusChange]);

  return {
    isOnline,
    hasPending,
    pendingCount: syncStatus.getPendingCount()
  };
}

/**
 * Dispatch custom event when pending sync status changes
 */
export function notifyPendingSyncChanged() {
  window.dispatchEvent(new CustomEvent('pendingSyncChanged'));
}
